import { P, S, T } from '../../../styles/tokens';
import { Check } from './Icons';

interface ConsentRowProps {
  /** Whether this consent is checked. */
  checked: boolean;
  /** Toggle handler. */
  onToggle: () => void;
  /** Main consent line — plain, human language. */
  text: string;
  /** Optional secondary explanation, smaller and muted. */
  subtext?: string;
  /** Optional tiny legal footnote (e.g. ФЗ reference) under an asterisk. */
  footnote?: string;
}

/** Single checkable consent row with plain copy.
 *  Legal references live in the tiny `footnote`, not a scary uppercase law badge:
 *  the law number is not a mandatory UI element under 152-ФЗ — only the consent
 *  content is (цель, перечень данных, срок, оператор). Keep this brand-neutral —
 *  it is the shared brick for every BRIEF checkpoint across all flows. */
export function ConsentRow({ checked, onToggle, text, subtext, footnote }: ConsentRowProps) {
  return (
    <button
      onClick={onToggle}
      data-testid="consent-row"
      style={{
        display: 'flex',
        gap: 10,
        alignItems: 'flex-start',
        background: checked ? '#EFF7E9' : '#fff',
        borderRadius: 12,
        padding: 12,
        border: `1.5px solid ${checked ? 'rgba(45,138,78,0.30)' : P.line}`,
        cursor: 'pointer',
        textAlign: 'left' as const,
        width: '100%',
        fontFamily: T.fontBody,
        transition: 'all 0.2s ease',
      }}
    >
      {/* Checkbox */}
      <div
        style={{
          width: 22,
          height: 22,
          borderRadius: 6,
          flexShrink: 0,
          background: checked ? P.success : '#fff',
          border: `1.5px solid ${checked ? P.success : P.muted}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginTop: 1,
        }}
      >
        {checked && (
          <span style={{ color: '#fff', display: 'flex' }}>
            <Check size={14} />
          </span>
        )}
      </div>
      {/* Label + text */}
      <div style={{ flex: 1 }}>
        <div style={{ font: T.footnote, fontWeight: 600, color: S.label, lineHeight: 1.5 }}>
          {text}
        </div>
        {subtext && (
          <div style={{ font: T.footnote, color: P.muted, lineHeight: 1.5, marginTop: 6 }}>
            {subtext}
          </div>
        )}
        {footnote && (
          <div style={{ font: T.captionAlt, color: S.tertiaryLabel, lineHeight: 1.4, marginTop: 6 }}>
            {footnote}
          </div>
        )}
      </div>
    </button>
  );
}
